import type { ReactNode } from "react";

/**
 * The app's icons, drawn here rather than installed.
 *
 * A dozen glyphs do not justify a dependency: an icon package is tens of
 * kilobytes and a supply chain, and FR-1.2's single mount is paid for by a
 * bundle nobody has to apologise for. They are stroked in the heroicons-outline
 * manner — 24×24, `currentColor`, round caps and joins — so that colour and
 * weight are the caller's to decide.
 *
 * Every one is `aria-hidden`: the label beside it, or the button's own
 * `aria-label`, says the same thing, and a screen reader announcing
 * "magnifier, Search" is reading the control twice.
 *
 * `className` REPLACES the default size when it carries one. It is spelled as a
 * fallback rather than as a prefix on purpose: two height utilities in one class
 * string are resolved by the order Tailwind emits them, not by the order they
 * were written, so `"h-6 w-6 " + "h-4 w-4"` silently stayed at 24px.
 */

export type IconProps = { className?: string; strokeWidth?: number };

function Glyph({
  children,
  className = "",
  strokeWidth = 1.5,
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      className={`shrink-0 ${className || "h-6 w-6"}`}
    >
      {children}
    </svg>
  );
}

/** One step back — a chevron, not an arrow: it is a step, not a journey home. */
export function BackIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.75 4.75 7.5 12l7.25 7.25" />
    </Glyph>
  );
}

/** One step in. The mirror of Back, and the settings row's "there is more". */
export function ForwardIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M9.25 4.75 16.5 12l-7.25 7.25" />
    </Glyph>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="10.75" cy="10.75" r="6" />
      <path d="m15.25 15.25 4 4" />
    </Glyph>
  );
}

/** New knock. The one thing in the sidebar that is not reading. */
export function PlusIcon(props: IconProps) {
  return (
    <Glyph {...props} strokeWidth={props.strokeWidth ?? 2.25}>
      <path d="M12 5.5v13M5.5 12h13" />
    </Glyph>
  );
}

/** Sort — three sliders, the way every list has said "arrange this" for years. */
export function SortIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />
      <circle cx="9" cy="7" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="8" cy="17" r="1.75" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

/**
 * The other things you can do with this conversation — three dots, horizontal.
 *
 * It is deliberately the quietest control in the header: what lives behind it
 * (ruling R-0828-1) is housekeeping, and housekeeping must not compete with the
 * four verbs that decide something about a person.
 */
export function MoreIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

/** The desk beside the conversation: a pane hinged off the right-hand edge. */
export function RailIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3.5" y="4.75" width="17" height="14.5" rx="2" />
      <path d="M14.5 4.75v14.5" />
    </Glyph>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6.25 6.25l11.5 11.5M17.75 6.25 6.25 17.75" />
    </Glyph>
  );
}

/** Hand out your door — a node passing to two others. */
export function ShareIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="17.5" cy="6" r="2.5" />
      <circle cx="6.5" cy="12" r="2.5" />
      <circle cx="17.5" cy="18" r="2.5" />
      <path d="m8.75 10.8 6.5-3.55M8.75 13.2l6.5 3.55" />
    </Glyph>
  );
}

/** Ask the doorman to do something to the rules. A wand, and a bit of glitter. */
export function SparkIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.5 4.5 16 8l3.5 1.5L16 11l-1.5 3.5L13 11 9.5 9.5 13 8Z" />
      <path d="m5 14 1 2.25L8.25 17.25 6 18.25 5 20.5 4 18.25 1.75 17.25 4 16.25Z" />
    </Glyph>
  );
}

/** A locked composer, and nothing else. Status, never decoration. */
export function LockIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="5" y="10.5" width="14" height="9" rx="2" />
      <path d="M8.25 10.5V8a3.75 3.75 0 0 1 7.5 0v2.5" />
    </Glyph>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Glyph {...props} strokeWidth={props.strokeWidth ?? 2}>
      <path d="m5.5 12.5 4.25 4.25L18.5 8" />
    </Glyph>
  );
}

/**
 * A cup, on the one row that leads to a ranking.
 *
 * The demand board is the only league table in the product, and it ranks
 * QUEUES rather than people — nobody on it has won anything. So the glyph
 * appears exactly once, on the way in, and never on a row.
 */
export function TrophyIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M7.5 4.75h9v4a4.5 4.5 0 0 1-9 0z" />
      <path d="M7.5 6.25H5.25v1.5a3 3 0 0 0 3 3M16.5 6.25h2.25v1.5a3 3 0 0 1-3 3" />
      <path d="M12 13.25v3.5M9 19.25h6" />
    </Glyph>
  );
}

/**
 * The provider's own mark, on the one button that leaves for it.
 *
 * It does not go through `Glyph`: every other icon here is a stroked outline
 * and this is a solid letterform, so it is drawn as a fill. It is the only
 * glyph in the app that belongs to somebody else, and it is used in exactly one
 * place — beside "Continue with X" — because that is where a reader is deciding
 * whether they recognise where they are about to be sent.
 */
export function XGlyph({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      className={`shrink-0 ${className || "h-4 w-4"}`}
    >
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}
